import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { TripProvider, useTrip } from './lib/tripContext.jsx';
import { UserProvider } from './lib/userContext.jsx';
import { SessionProvider, useSession } from './lib/sessionContext.jsx';
import { TestModeProvider } from './lib/testModeContext.jsx';
import Header from './components/Header.jsx';
import BottomTabBar from './components/BottomTabBar.jsx';
import TestModeBanner from './components/TestModeBanner.jsx';
import TripHub from './tabs/TripHub.jsx';
import Today from './tabs/Today.jsx';
import Activities from './tabs/Activities.jsx';
import WhaleWatching from './tabs/WhaleWatching.jsx';
import EndicottArm from './tabs/EndicottArm.jsx';
import Drives from './tabs/Drives.jsx';
import Meals from './tabs/Meals.jsx';
import Excursions from './tabs/Excursions.jsx';
import MendenhallCanoe from './tabs/MendenhallCanoe.jsx';
import Costs from './tabs/Costs.jsx';
import TripRoll from './tabs/TripRoll.jsx';
import TransmissionsLog from './tabs/TransmissionsLog.jsx';
import PhotoCoaching from './tabs/PhotoCoaching.jsx';
import Settings from './tabs/Settings.jsx';
import Wilman from './tabs/Wilman.jsx';
import FieldAssistant from './tabs/FieldAssistant.jsx';
import Packing from './tabs/Packing.jsx';
import SOS from './tabs/SOS.jsx';
import More from './pages/More.jsx';
import Archive from './pages/Archive.jsx';
import Briefing from './pages/Briefing.jsx';
import Onboarding from './pages/Onboarding.jsx';
import Slideshow from './pages/Slideshow.jsx';
import Spectator from './tabs/Spectator.jsx';

function Shell() {
  const { trip, error } = useTrip();
  const location = useLocation();
  const session = useSession();

  // Briefing + onboarding + slideshow use a standalone layout — no
  // shell chrome, no tab bar. Briefing is the first thing recipients
  // see from the email; onboarding walks install + push; slideshow is
  // a permanent shareable URL that stands alone (spec §13).
  if (
    location.pathname.startsWith('/briefing/') ||
    location.pathname === '/onboarding' ||
    location.pathname.startsWith('/slideshow/') ||
    location.pathname === '/archive'
  ) {
    return (
      <Routes>
        <Route path="/archive" element={<Archive />} />
        <Route path="/briefing/:token" element={<Briefing />} />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/slideshow/:tripId" element={<Slideshow />} />
      </Routes>
    );
  }

  // Role-aware home: spectators land on the dashboard when they hit /,
  // not the player-oriented Trip Hub. Wait until session resolves so we
  // don't flash Trip Hub for a frame before the redirect.
  if (location.pathname === '/' && !session.loading && session.role === 'spectator-judge') {
    return <Navigate to="/spectator" replace />;
  }

  if (error) {
    return (
      <div className="flex h-full items-center justify-center p-6 text-fg-secondary">
        <div className="card max-w-sm p-6 text-center">
          <h1 className="font-mono text-sm uppercase tracking-wider text-danger">
            transmission error
          </h1>
          <p className="mt-2 text-base">Could not load trip configuration.</p>
          <p className="mt-1 font-mono text-xs text-fg-tertiary">{String(error.message)}</p>
        </div>
      </div>
    );
  }

  if (!trip) {
    return (
      <div className="flex h-full items-center justify-center text-fg-secondary">
        <span className="font-mono text-xs uppercase tracking-wider">…tuning in</span>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <TestModeBanner />
      <Header />
      <main className="scroll-area flex-1 overflow-y-auto">
        <Routes>
          <Route path="/" element={<TripHub />} />
          <Route path="/today" element={<Today />} />
          <Route path="/activities" element={<Activities />} />
          <Route path="/wilman" element={<Wilman />} />
          <Route path="/spectator" element={<Spectator />} />
          <Route path="/more" element={<More />} />
          <Route path="/drives" element={<Drives />} />
          <Route path="/meals" element={<Meals />} />
          <Route path="/excursions" element={<Excursions />} />
          <Route path="/excursions/whale-watching" element={<WhaleWatching />} />
          <Route path="/excursions/mendenhall-canoe" element={<MendenhallCanoe />} />
          <Route path="/excursions/endicott-arm" element={<EndicottArm />} />
          {/* Backwards-compat: old direct URLs from links shared before
              the umbrella reorg. Redirect to the nested routes. */}
          <Route path="/whale-watching" element={<Navigate to="/excursions/whale-watching" replace />} />
          <Route path="/endicott-arm" element={<Navigate to="/excursions/endicott-arm" replace />} />
          <Route path="/field-assistant" element={<FieldAssistant />} />
          <Route path="/trip-roll" element={<TripRoll />} />
          <Route path="/transmissions" element={<TransmissionsLog />} />
          <Route path="/photo-coaching" element={<PhotoCoaching />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/costs" element={<Costs />} />
          <Route path="/packing" element={<Packing />} />
          <Route path="/sos" element={<SOS />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <BottomTabBar />
    </div>
  );
}

export default function App() {
  return (
    <TripProvider>
      <UserProvider>
        <SessionProvider>
          <TestModeProvider>
            <Shell />
          </TestModeProvider>
        </SessionProvider>
      </UserProvider>
    </TripProvider>
  );
}
